import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { checksAPI, banksAPI } from '../services/api';

const statusLabels = {
  draft: 'Brouillon',
  pending: 'En attente',
  cleared: 'Encaissé',
  bounced: 'Impayé',
  cancelled: 'Annulé',
};

const statusColors = {
  draft: 'default',
  pending: 'warning',
  cleared: 'success',
  bounced: 'error',
  cancelled: 'default',
};

const steps = ['Téléverser le relevé', 'Révision des correspondances', 'Confirmation'];

function CheckReconcileModal({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [pendingChecks, setPendingChecks] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [statementFile, setStatementFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [updatedBalanceMsg, setUpdatedBalanceMsg] = useState('');

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setStatementFile(null);
      setParsedRows([]);
      setMatches([]);
      setError(null);
      setSelectedBankId('');
      setUpdatedBalanceMsg('');
      loadBanks();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      loadPendingChecks();
    }
  }, [selectedBankId, open]);

  const loadBanks = async () => {
    try {
      const response = await banksAPI.getAll();
      setBanks(response.data || []);
    } catch (err) {
      console.error('Error loading banks:', err);
    }
  };

  const loadPendingChecks = async () => {
    try {
      setLoading(true);
      const params = { status: 'pending' };
      if (selectedBankId) {
        params.bank = selectedBankId;
      }
      const response = await checksAPI.getAll(params);
      setPendingChecks(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error loading pending checks:', err);
      setError('Échec du chargement des chèques en attente');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setStatementFile(e.target.files[0]);
    }
  };

  const handleUploadStatement = async () => {
    if (!statementFile) return;
    if (!selectedBankId) {
      setError('Veuillez d\'abord sélectionner une banque.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const formData = new FormData();
      formData.append('file', statementFile);
      formData.append('bank_id', selectedBankId);
      const response = await checksAPI.importStatement(formData);
      setParsedRows(response.data.rows || []);
      
      if (response.data.updated_balance && response.data.bank_details) {
        setUpdatedBalanceMsg(`La liquidité de la banque "${response.data.bank_details.name}" a été mise à jour automatiquement à ${parseFloat(response.data.updated_balance).toLocaleString()} ${response.data.bank_details.currency} à partir du relevé bancaire.`);
      }

      setActiveStep(1);
      autoMatch(response.data.rows || []);
    } catch (err) {
      console.error('Error importing statement:', err);
      setError(err.response?.data?.detail || 'Échec de l\'importation du relevé bancaire');
    } finally {
      setLoading(false);
    }
  };

  const autoMatch = (rows) => {
    const newMatches = [];
    rows.forEach((row, idx) => {
      const check = pendingChecks.find((c) => {
        const refMatch = c.check_number && row.reference &&
          c.check_number.toLowerCase().includes(row.reference.toLowerCase());
        const amountMatch = Math.abs(parseFloat(c.amount) - Math.abs(parseFloat(row.amount))) < 0.01;
        return refMatch || amountMatch;
      });
      if (check) {
        newMatches.push({
          rowIndex: idx,
          checkId: check.id,
          checkNumber: check.check_number,
          rowAmount: row.amount,
          rowDate: row.date,
          rowDescription: row.description,
          checkAmount: check.amount,
          status: 'proposed',
        });
      }
    });
    setMatches(newMatches);
  };

  const handleMatchChange = (rowIndex, checkId) => {
    const check = pendingChecks.find((c) => c.id === checkId);
    const row = parsedRows[rowIndex];
    const existingIdx = matches.findIndex((m) => m.rowIndex === rowIndex);

    if (!checkId) {
      if (existingIdx >= 0) {
        const updated = [...matches];
        updated.splice(existingIdx, 1);
        setMatches(updated);
      }
      return;
    }

    const newMatch = {
      rowIndex,
      checkId: check.id,
      checkNumber: check.check_number,
      rowAmount: row.amount,
      rowDate: row.date,
      rowDescription: row.description,
      checkAmount: check.amount,
      status: 'proposed',
    };

    if (existingIdx >= 0) {
      const updated = [...matches];
      updated[existingIdx] = newMatch;
      setMatches(updated);
    } else {
      setMatches((prev) => [...prev, newMatch]);
    }
  };

  const handleReconcile = async () => {
    try {
      setReconciling(true);
      setError(null);

      const reconcileData = matches.map((m) => ({
        check_id: m.checkId,
        statement_row: parsedRows[m.rowIndex],
      }));

      await checksAPI.reconcile({ matches: reconcileData });
      setActiveStep(2);
      setError(null);
    } catch (err) {
      console.error('Error reconciling:', err);
      setError(err.response?.data?.detail || 'Échec de la réconciliation');
    } finally {
      setReconciling(false);
    }
  };

  const isMatched = (rowIndex) => matches.some((m) => m.rowIndex === rowIndex);
  const getMatch = (rowIndex) => matches.find((m) => m.rowIndex === rowIndex);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BankIcon color="primary" />
            <Typography variant="h6">Réconciliation bancaire</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                size="small"
                options={banks}
                getOptionLabel={(opt) => `${opt.name} (${opt.current_balance?.toLocaleString()} ${opt.currency})`}
                value={banks.find((b) => b.id === selectedBankId) || null}
                onChange={(e, val) => setSelectedBankId(val?.id || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Sélectionner la banque correspondante *" placeholder="Rechercher une banque..." required />
                )}
                fullWidth
              />
            </Box>
            <Typography variant="subtitle1" gutterBottom>
              Chèques en attente: {pendingChecks.length}
            </Typography>
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Button variant="outlined" component="label" sx={{ py: 1 }} disabled={!selectedBankId}>
                <UploadIcon sx={{ mr: 1 }} />
                {statementFile ? statementFile.name : 'Téléverser le relevé (Excel/CSV)'}
                <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFileChange} />
              </Button>
              {statementFile && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleUploadStatement}
                    disabled={loading || !selectedBankId}
                    startIcon={loading ? <CircularProgress size={16} /> : <UploadIcon />}
                  >
                    {loading ? 'Importation en cours...' : 'Importer'}
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Lignes du relevé: {parsedRows.length} | Correspondances proposées: {matches.length}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Référence</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Montant</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Chèque correspondant</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedRows.map((row, idx) => {
                    const match = getMatch(idx);
                    return (
                      <TableRow key={idx} hover selected={!!match}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.reference}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.type === 'outgoing' ? 'Sortant' : 'Entrant'}
                            color={row.type === 'outgoing' ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={[{ id: '', check_number: '-- Aucune correspondance --' }, ...pendingChecks]}
                            getOptionLabel={(opt) => opt.check_number || ''}
                            value={match ? pendingChecks.find((c) => c.id === match.checkId) || { id: '', check_number: '' } : { id: '', check_number: '' }}
                            onChange={(e, val) => handleMatchChange(idx, val?.id || '')}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Sélectionner un chèque" size="small" />
                            )}
                            sx={{ minWidth: 180 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setActiveStep(0)}>
                Retour
              </Button>
              <Button
                variant="contained"
                onClick={handleReconcile}
                disabled={reconciling || matches.length === 0}
                startIcon={reconciling ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              >
                {reconciling ? 'Réconciliation en cours...' : 'Confirmer la réconciliation'}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5" sx={{ mt: 2 }}>
              Réconciliation réussie!
            </Typography>
            {updatedBalanceMsg && (
              <Alert severity="info" sx={{ mt: 2, mb: 2, mx: 'auto', maxWidth: '80%' }}>
                {updatedBalanceMsg}
              </Alert>
            )}
            <Typography variant="body1" color="text.secondary">
              Les statuts des chèques correspondants ont été mis à jour.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={onClose}>
                Fermer
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      {activeStep !== 2 && (
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default CheckReconcileModal;
